import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  X, PencilLine, Loader2, Save, FileText,
  ExternalLink, FolderOpen, Image as ImageIcon,
  Users2, Briefcase, Building2, Trash2
} from "lucide-react";
import Swal from "sweetalert2";
import TooltipHelper from "../../../../components/common/TooltipHelper";
import {
  getMembershipPdfTemplate,
  updateMembershipAnswers,
  regenerateMembershipPdf,
  getMembershipApplication,
} from "../../../../services/membershipApplications";
import { fetchWithAuth } from "../../../../utils/fetchWithAuth";

// ⬇️ Opciones y regiones
import { industries as INDUSTRIES, periodicities as PERIODICITIES } from "../../../../constants/options";
import { regionsData } from "../../../../constants/regiones";

/* ============================================================
   CONFIG: nombre del archivo DOCX que está en el /public del FRONT
============================================================ */
const DECLARATION_FILE_NAME = "Formato Declaración Ingreso Cidere Región de Coquimbo.docx";

/* ---------- UI helpers ---------- */
function DecisionBar({ status }) {
  const base = "inline-flex items-center gap-2 rounded-xl border px-2 h-7 min-w-0";
  if (status === "ACCEPTED") {
    return (
      <div className={`${base} border-green-200 bg-green-50`}>
        <span className="inline-flex w-2 h-2 rounded-full bg-green-600" />
        <span className="text-green-800 font-semibold text-[11px] uppercase tracking-wide">Aceptada</span>
      </div>
    );
  }
  if (status === "REJECTED") {
    return (
      <div className={`${base} border-rose-200 bg-rose-50`}>
        <span className="inline-flex w-2 h-2 rounded-full bg-rose-600" />
        <span className="text-rose-800 font-semibold text-[11px] uppercase tracking-wide">Rechazada</span>
      </div>
    );
  }
  if (status === "WAITING_RESPONSE") {
    return (
      <div className={`${base} border-blue-200 bg-blue-50`}>
        <span className="inline-flex w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
        <span className="text-blue-800 font-semibold text-[11px] uppercase tracking-wide">Esperando respuesta</span>
      </div>
    );
  }
  return (
    <div className={`${base} border-amber-200 bg-amber-50`}>
      <span className="inline-flex w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
      <span className="text-amber-800 font-semibold text-[11px] uppercase tracking-wide">Pendiente</span>
    </div>
  );
}

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`h-10 px-3 rounded-lg text-sm font-medium border ${
      active ? "bg-white text-[#0b3a66] border-gray-200 shadow-sm"
             : "bg-gray-50 text-gray-600 border-transparent hover:bg-white hover:border-gray-200"
    }`}
  >
    {children}
  </button>
);

/* ====== HELPERS: parse + colector robusto de links ====== */
const safeParseJson = (v) => {
  if (!v) return {};
  if (typeof v === "object") return v;
  try { return JSON.parse(v); } catch { return {}; }
};

const URL_FIELDS  = ["viewUrl","secureUrl","secure_url","fileUrl","downloadUrl","url","href","link"];
const NAME_FIELDS = ["name","fileName","filename","originalName","originalname","original_filename","publicId","public_id","key"];

const isUrlLike = (s) => typeof s === "string" && /^https?:\/\//i.test(s);

function fileNameFromUrl(u="") {
  try {
    const p = new URL(u);
    const last = p.pathname.split("/").filter(Boolean).pop() || "";
    return decodeURIComponent(last) || u;
  } catch {
    const clean = u.split("?")[0].split("#")[0];
    return clean.split("/").pop() || u;
  }
}

function cloudThumb(href, { w = 320, h = 200 } = {}) {
  try {
    const u = new URL(href);
    if (!/res\.cloudinary\.com$/i.test(u.hostname)) return href;
    const parts = u.pathname.split("/upload/");
    if (parts.length !== 2) return href;
    const left = parts[0];
    const right = parts[1];
    let segment = right;
    if (right.includes("/")) {
      const [maybeTransforms, rest] = right.split(/\/(.+)/);
      const hasTransform = /[a-z]\_/.test(maybeTransforms);
      segment = hasTransform
        ? `fl_force_strip,f_auto,q_auto,c_limit,w_${w},h_${h},${maybeTransforms}/${rest}`
        : `c_limit,w_${w},h_${h},f_auto,q_auto/${right}`;
    } else {
      segment = `c_limit,w_${w},h_${h},f_auto,q_auto/${right}`;
    }
    u.pathname = `${left}/upload/${segment}`;
    return u.toString();
  } catch {
    return href;
  }
}

function asDownloadUrl(href) {
  try {
    const u = new URL(href);
    if (!/res\.cloudinary\.com$/i.test(u.hostname)) return href;
    const parts = u.pathname.split("/upload/");
    if (parts.length !== 2) return href;
    const right = parts[1];
    const already = right.startsWith("fl_attachment") || right.startsWith("fl_attachment,");
    const out = already ? right : `fl_attachment,${right}`;
    u.pathname = `${parts[0]}/upload/${out}`;
    return u.toString();
  } catch {
    return href;
  }
}

function normalizeImgUrl(href) {
  try {
    const u = new URL(href);
    if (!/res\.cloudinary\.com$/i.test(u.hostname)) return href;
    const last = u.pathname.split("/").pop() || "";
    if (/\.[a-z0-9]{2,5}$/i.test(last)) return href;
    u.pathname = `${u.pathname}.png`;
    return u.toString();
  } catch {
    return href;
  }
}

function collectFileEntries(input, depth=0, out=[], seen=new Set()) {
  if (depth > 4 || input == null) return out;

  if (typeof input === "string") {
    if (isUrlLike(input)) {
      if (!seen.has(input)) {
        seen.add(input);
        out.push({ href: input, label: fileNameFromUrl(input) });
      }
    }
    return out;
  }

  if (Array.isArray(input)) {
    input.forEach(it => collectFileEntries(it, depth+1, out, seen));
    return out;
  }

  if (typeof input === "object") {
    let href;
    for (const k of URL_FIELDS) {
      if (isUrlLike(input[k])) { href = input[k]; break; }
    }
    if (!href) {
      for (const k in input) {
        const v = input[k];
        if (typeof v === "string" && isUrlLike(v)) { href = v; break; }
      }
    }
    if (href && !seen.has(href)) {
      seen.add(href);
      let label;
      for (const k of NAME_FIELDS) {
        if (typeof input[k] === "string" && input[k].trim()) { label = input[k]; break; }
      }
      if (!label) label = fileNameFromUrl(href);
      out.push({ href, label });
    }
    for (const k in input) {
      if (input[k] && typeof input[k] === "object") collectFileEntries(input[k], depth+1, out, seen);
    }
  }
  return out;
}

/* --- Humanización & coalesce --- */
const coalesce = (...vals) =>
  vals.find(v => v != null && String(v).trim() !== "") ?? "";
const valOrDash = (v) => (v && String(v).trim() !== "" ? v : "—");

const humanSize = (v) => {
  const s = (v || "").toString().toUpperCase();
  if (["MICRO","SMALL","MEDIUM","LARGE", "BIG"].includes(s)) {
    return { MICRO:"Micro", SMALL:"Pequeña", MEDIUM:"Mediana", LARGE:"Grande", BIG:"Grande" }[s];
  }
  return s || "";
};

// Mapear tamaño de inglés a español para el formulario de edición
const mapSizeToSpanish = (v) => {
  if (!v) return "";
  const s = (v || "").toString().toLowerCase().trim();
  if (s === "micro") return "Micro";
  if (s === "small") return "Pequeña";
  if (s === "medium") return "Mediana";
  if (s === "large" || s === "big") return "Grande";
  // Si ya está en español, retornarlo tal cual (comparación case-insensitive)
  const vStr = String(v).trim();
  const spanishOptions = ["Micro", "Pequeña", "Mediana", "Grande"];
  const found = spanishOptions.find(opt => opt.toLowerCase() === vStr.toLowerCase());
  if (found) return found;
  return vStr;
};

// Mapear tamaño de español a inglés para guardar (o mantener si ya está en inglés)
const mapSizeToEnglish = (v) => {
  if (!v) return "";
  const s = (v || "").toString().toLowerCase().trim();
  if (s === "micro") return "Micro";
  if (s === "pequeña" || s === "pequena") return "Small";
  if (s === "mediana") return "Medium";
  if (s === "grande") return "Big";
  // Si ya está en inglés, retornarlo tal cual
  if (["Micro", "Small", "Medium", "Big", "Large"].includes(v)) return v === "Large" ? "Big" : v;
  return v;
};
const humanWorkers = (v) => {
  const s = (v || "").toString().toUpperCase();
  if (["W_0_9","W_10_49","W_50_199","W_200_PLUS"].includes(s)) {
    const map = { W_0_9: "0–9", W_10_49: "10–49", W_50_199: "50–199", W_200_PLUS: "200+" };
    return `${map[s]} Trabajadores`;
  }
  if (/^\d+\s*[-–]\s*\d+$/.test(s)) return `${s.replace(/\s+/g, "")} Trabajadores`;
  if (/^\d+\+$/.test(s)) return `${s} Trabajadores`;
  return s;
};
const humanPeriodicity = (v) => {
  const s = (v || "").toString().toUpperCase();
  if (["MENSUAL","SEMESTRAL","ANUAL","ANNUAL"].includes(s)) {
    return { MENSUAL:"Mensual", SEMESTRAL:"Semestral", ANUAL:"Anual", ANNUAL:"Anual" }[s];
  }
  return s || "";
};

/* ===== Helper para construir la URL pública de la DJ en el FRONT ===== */
function getFrontendPublicUrl(fileName) {
  const origin = (typeof window !== "undefined" && window.location && window.location.origin) || "";
  if (!origin) return "";
  const clean = String(fileName || "").replace(/^\/+/, "");
  return `${origin}/${encodeURIComponent(clean)}`;
}

/* =================== VENTANA SUPERPUESTA =================== */
export default function DetailsModal({ app, open, onClose, onChanged, onBusy, allowEditWhenAccepted = false }) {
  const [tab, setTab] = useState("DETAIL");
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [tpl, setTpl] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [allowEditMembresia, setAllowEditMembresia] = useState(false);
  // Flag para rastrear si el valor de membresía fue editado manualmente desde "Valor de Membresía"
  const valorMembresiaEditadoManualRef = React.useRef(false);

  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  // Ref para rastrear si la ventana superpuesta estaba abierta anteriormente
  const wasOpenRef = React.useRef(false);

  useEffect(() => {
    // Solo resetear la pestaña cuando la ventana superpuesta se abre por primera vez (de false a true)
    // No resetear cuando app cambia mientras la ventana superpuesta ya está abierta
    if (open && !wasOpenRef.current) {
      setTab("DETAIL");
      wasOpenRef.current = true;
    } else if (!open) {
      wasOpenRef.current = false;
    }
    
    // Resetear otros estados solo cuando la ventana superpuesta se cierra
    if (!open) {
      setTpl(null);
      setEditMode(false);
      setAllowEditMembresia(false);
      valorMembresiaEditadoManualRef.current = false;
    }

    (async () => {
      if (!open || !app?.id) { setDetail(null); return; }
      try {
        setDetailLoading(true);
        const res = await getMembershipApplication(app.id);
        const loaded = res?.data || res || null;
        setDetail(loaded);
      } catch (e) {
        console.warn("No se pudo cargar detalle:", e?.message || e);
      } finally {
        setDetailLoading(false);
      }
    })();
  }, [open, app]);

  // Función helper para calcular valor de UF según tamaño (con coma para decimales)
  // Acepta tanto español como inglés
  const calcularValorUF = useCallback((tamano) => {
    if (!tamano) return "";
    const s = (tamano || "").toLowerCase().trim();
    // Micro (español e inglés)
    if (s === "micro") return "2";
    // Pequeña/Small (español e inglés)
    if (s.includes("peque") || s === "small") return "2,5";
    // Mediana/Medium (español e inglés)
    if (s.includes("median") || s === "medium") return "5";
    // Grande/Big/Large (español e inglés)
    if (s.includes("grande") || s === "big" || s === "large") return "10";
    return "";
  }, []);

  // Función helper para extraer solo el número del valor de membresía (acepta coma o punto)
  const extraerNumero = useCallback((valor) => {
    if (!valor) return "";
    // Extraer solo números, coma y punto decimal (convertir punto a coma)
    const match = String(valor).match(/[\d,.]+/);
    if (!match) return "";
    // Convertir punto a coma para consistencia
    return match[0].replace(/\./g, ',');
  }, []);

  const loadTemplate = useCallback(async () => {
    const id = (detail?.id ?? app?.id);
    if (!id) return;
    setLoadingTpl(true);
    try {
      const res = await getMembershipPdfTemplate(id);
      const loadedTpl = res?.data || res || null;
      
      // Auto-rellenar valor de membresía automáticamente si hay tamaño pero no valor (solo número)
      if (loadedTpl?.empresa?.tamano && !loadedTpl?.empresa?.valor_membresia) {
        const autoValue = calcularValorUF(loadedTpl.empresa.tamano);
        if (autoValue) {
          loadedTpl.empresa.valor_membresia = autoValue; // Solo número, sin "UF"
        }
      }
      
      // Limpiar valor existente para quitar "UF" si está presente
      if (loadedTpl?.empresa?.valor_membresia) {
        const numero = extraerNumero(loadedTpl.empresa.valor_membresia);
        if (numero) {
          loadedTpl.empresa.valor_membresia = numero;
        }
      }

      // Inicializar fecha si no existe (por defecto año actual)
      if (!loadedTpl?.empresa?.fecha) {
        const añoActual = new Date().getFullYear().toString();
        loadedTpl.empresa.fecha = añoActual;
      }
      
      setTpl(loadedTpl);
      // Reset flag cuando se carga el template
      valorMembresiaEditadoManualRef.current = false;
    } catch (e) {
      Swal.fire("Error", e?.message || "No se pudo cargar la plantilla", "error");
    } finally {
      setLoadingTpl(false);
    }
  }, [detail?.id, app?.id, calcularValorUF]);

  // Importante: solo editable en estado "pendiente" (tu backend lo llama RECEIVED)
  // Pero si allowEditWhenAccepted es true, también se puede editar cuando está ACCEPTED
  const status = detail?.status || app?.status;
  const canEdit = status === "RECEIVED" || (allowEditWhenAccepted && status === "ACCEPTED");

  const setField = useCallback((path, value) => {
    setTpl((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      const parts = path.split(".");
      let node = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        if (!node[p]) node[p] = {};
        node = node[p];
      }
      node[parts[parts.length - 1]] = value;
      return next;
    });
  }, []);

  // Cargar template cuando se cambia a la pestaña MEMBRESIA
  // También cargar si allowEditWhenAccepted es true y está en ACCEPTED
  useEffect(() => {
    if (tab === "MEMBRESIA" && !tpl && canEdit) {
      loadTemplate();
    }
  }, [tab, tpl, canEdit, loadTemplate, allowEditWhenAccepted]);

  // Auto-rellenar valor de UF cuando se carga el template inicialmente (solo si no hay valor)
  useEffect(() => {
    if (tpl?.empresa?.tamano && !tpl?.empresa?.valor_membresia && canEdit && editMode) {
      const autoValue = calcularValorUF(tpl.empresa.tamano);
      if (autoValue) {
        setField("empresa.valor_membresia", autoValue); // Solo número, sin "UF"
      }
    }
  }, [tpl?.empresa?.tamano, tpl?.empresa?.valor_membresia, canEdit, editMode, calcularValorUF, setField]);

  // Sincronizar valor de membresía cuando cambia el tamaño (SIEMPRE actualizar, incluso si ya existe valor)
  // PERO solo si NO fue editado manualmente desde "Valor de Membresía"
  // Usamos useRef para rastrear el último tamaño y evitar loops infinitos
  const lastTamanoRef = React.useRef(null);
  useEffect(() => {
    if (tpl?.empresa?.tamano && canEdit && editMode && !valorMembresiaEditadoManualRef.current) {
      const tamanoActual = String(tpl.empresa.tamano).trim();
      // Solo actualizar si el tamaño realmente cambió
      if (lastTamanoRef.current !== tamanoActual) {
        lastTamanoRef.current = tamanoActual;
        const autoValue = calcularValorUF(tpl.empresa.tamano);
        if (autoValue) {
          // Verificar si el valor actual no coincide con el esperado para este tamaño
          const valorActual = String(tpl.empresa?.valor_membresia || "").replace(/\./g, ',').trim();
          const valorEsperado = autoValue.trim();
          // Solo actualizar si son diferentes
          if (valorActual !== valorEsperado) {
            setField("empresa.valor_membresia", autoValue);
          }
        }
      }
    }
  }, [tpl?.empresa?.tamano, canEdit, editMode, calcularValorUF, setField]);

  const startEdit = async () => {
    if (!canEdit) {
      const statusText = status === "ACCEPTED" ? "ACEPTADA" : status === "REJECTED" ? "RECHAZADA" : "en otro estado";
      Swal.fire("No se puede editar", `No puedes editar cuando la postulación está ${statusText}.`, "info");
      return;
    }
    if (!tpl) await loadTemplate();
    // Reset flag cuando se inicia la edición
    valorMembresiaEditadoManualRef.current = false;
    lastTamanoRef.current = null;
    setEditMode(true);
  };

  const onSave = async () => {
    const id = (detail?.id ?? app?.id);
    if (!tpl || !id) return;
    
    // Mostrar mensaje de carga
    const loadingToast = Swal.fire({
      title: "Guardando cambios...",
      html: "Por favor espera mientras se guardan los cambios y se regeneran los PDFs.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      setSaving(true);
      onBusy?.(true);

      // Preparar template para enviar: normalizar a coma (el backend ahora usa coma)
      const tplToSend = structuredClone(tpl);
      
      // Asegurar que el valor de membresía esté sincronizado con el tamaño antes de guardar
      // PERO solo si NO fue editado manualmente desde "Valor de Membresía"
      if (tplToSend?.empresa?.tamano && !valorMembresiaEditadoManualRef.current) {
        const autoValue = calcularValorUF(tplToSend.empresa.tamano);
        if (autoValue) {
          // Si hay un tamaño, asegurar que el valor de membresía esté actualizado
          const valorActual = String(tplToSend.empresa?.valor_membresia || "").replace(/\./g, ',').trim();
          const valorEsperado = autoValue.trim();
          if (valorActual !== valorEsperado) {
            tplToSend.empresa.valor_membresia = autoValue;
          }
        }
      }
      
      if (tplToSend?.empresa?.valor_membresia) {
        // Convertir punto a coma para normalizar (todo se almacena con coma)
        tplToSend.empresa.valor_membresia = String(tplToSend.empresa.valor_membresia).replace(/\./g, ',');
      }

      // 1) Guardar respuestas
      await updateMembershipAnswers(id, tplToSend);

      // 2) Regenerar PDF (NO silenciar errores)
      const regen = await regenerateMembershipPdf(id);
      const updated = regen?.data || regen || null;

      // 3) Validar que realmente llegó alguna URL del PDF nuevo
      const hasUrl =
        updated?.documentUrl ||
        updated?.cloudinaryDownloadUrl ||
        updated?.coverImageUrl ||
        updated?.answers?.cloudinary?.viewUrl ||
        updated?.answers?.cloudinary?.secureUrl ||
        updated?.answers?.cloudinary?.downloadUrl;

      if (!hasUrl) {
        throw new Error("El backend no devolvió la URL del PDF regenerado.");
      }

      // 4) Recargar el detalle completo desde el backend para asegurar que todos los campos estén actualizados
      const refreshed = await getMembershipApplication(id);
      const fullDetail = refreshed?.data || refreshed || updated;

      // 5) Refrescar en UI con el detalle completo
      setDetail(fullDetail);
      onChanged?.(fullDetail);
      
      // 6) Actualizar también el template para que refleje los cambios
      if (fullDetail?.answers) {
        const updatedTpl = safeParseJson(fullDetail.answers);
        if (updatedTpl?.empresa) {
          setTpl(updatedTpl);
        }
      }
      
      // Reset flag después de guardar exitosamente
      valorMembresiaEditadoManualRef.current = false;
      lastTamanoRef.current = null;
      
      // Cerrar mensaje de carga y mostrar éxito
      await Swal.fire({
        icon: "success",
        title: "Guardado",
        text: "Cambios guardados y PDF regenerado correctamente.",
        timer: 2000,
        showConfirmButton: false,
      });
      
      // Cerrar el modo de edición según la pestaña actual
      if (tab === "MEMBRESIA") {
        // Si estamos en MEMBRESIA, cerrar el modo de edición de membresía
        setAllowEditMembresia(false);
      } else {
        // Si estamos en otra pestaña (DETAIL), cerrar editMode
        setEditMode(false);
      }
    } catch (e) {
      Swal.fire("Error", e?.message || "No se pudo guardar o regenerar el PDF", "error");
    } finally {
      setSaving(false);
      onBusy?.(false);
    }
  };

  // ======= DOCS tab actions =======
  const onGenerate = async () => {
    const id = (detail?.id ?? app?.id);
    if (!id) return;
    const current = (detail || app || {});
    const confirm = await Swal.fire({
      title: current?.documentUrl ? "Regenerar PDF" : "Generar PDF",
      text: current?.documentUrl
        ? "Se volverá a generar el PDF de la ficha de postulación y se subirá a Cloudinary."
        : "Se generará el PDF de la ficha de postulación con los datos actuales y se subirá a Cloudinary.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: current?.documentUrl ? "Regenerar" : "Generar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0488AF",
      cancelButtonColor: "#C42251",
    });

    if (!confirm.isConfirmed) return;

    // Mostrar mensaje de carga
    const loadingToast = Swal.fire({
      title: "Generando PDF...",
      html: "Por favor espera mientras se genera el PDF de la ficha de postulación.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      setGenerating(true);
      onBusy?.(true);
      const res = await regenerateMembershipPdf(id);
      const updated = res?.data || res;
      onChanged?.(updated);
      setDetail((d) => ({ ...(d || {}), ...(updated || {}) }));
      
      // Cerrar mensaje de carga y mostrar éxito
      await Swal.fire({
        icon: "success",
        title: "Listo",
        text: "PDF procesado correctamente.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire("Error", e?.message || "No se pudo procesar el PDF", "error");
    } finally {
      setGenerating(false);
      onBusy?.(false);
    }
  };

  const deleteMembershipPdf = async (id) => {
    return fetchWithAuth(`/membership-applications/${id}/pdf`, { method: "DELETE" });
  };

  const onDeletePdf = async () => {
    const id = (detail?.id ?? app?.id);
    if (!id) return;
    const confirm = await Swal.fire({
      title: "Eliminar PDF",
      text: "Se eliminará la ficha generada en Cloudinary. Podrás generarla otra vez cuando quieras.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#C42251",
    });
    if (!confirm.isConfirmed) return;

    try {
      onBusy?.(true);
      const res = await deleteMembershipPdf(id);
      const ok = res?.ok ?? (res?.status >= 200 && res?.status < 300);
      if (!ok) {
        let data = null;
        try { data = await res.json(); } catch {}
        throw new Error(data?.message || "No se pudo eliminar");
      }
      const cleaned = {
        ...(detail || app || {}),
        documentUrl: null,
        cloudinaryPublicId: null,
        cloudinaryVersion: null,
        cloudinaryDownloadUrl: null,
        coverImageUrl: null,
        answers: {
          ...(safeParseJson((detail || app || {}).answers)),
          cloudinary: {},
        },
      };
      setDetail(cleaned);
      onChanged?.(cleaned);
      Swal.fire("Eliminado", "Se eliminó el PDF de la ficha de postulación correctamente.", "success");
    } catch (e) {
      Swal.fire("Error", e?.message || "No se pudo eliminar el PDF de la ficha de postulación", "error");
    } finally {
      onBusy?.(false);
    }
  };

  // ===== Derivadas =====
  const src = useMemo(() => detail || app || {}, [detail, app]);
  const ans = useMemo(() => safeParseJson(src.answers), [src.answers]);

  // Helper para obtener razón social
  const razonSocial = useMemo(() => {
    return src?.razonSocial || ans?.empresa?.razon_social || '';
  }, [src, ans]);

  // Helper para generar nombre de archivo con razón social
  const generateFileNameWithRazonSocial = (baseName, defaultName) => {
    const razon = razonSocial?.trim();
    if (!razon) {
      return baseName || defaultName;
    }
    // Limpiar razón social para usar como nombre de archivo (remover caracteres especiales)
    const cleanRazon = razon
      .replace(/[<>:"/\\|?*]/g, '_') // Reemplazar caracteres no permitidos
      .replace(/\s+/g, '_') // Reemplazar espacios con guiones bajos
      .substring(0, 50); // Limitar longitud
    // Obtener extensión del nombre base o usar la del default
    const ext = baseName?.match(/\.[^.]+$/) || defaultName?.match(/\.[^.]+$/) || '.pdf';
    const baseWithoutExt = baseName?.replace(/\.[^.]+$/, '') || defaultName?.replace(/\.[^.]+$/, '') || 'documento';
    return `${baseWithoutExt}_${cleanRazon}${ext}`;
  };

  const pdfInfo = useMemo(() => {
    const cloud = ans?.cloudinary || {};
    const view =
      src.documentUrl ||
      cloud.viewUrl ||
      cloud.secureUrl ||
      cloud.downloadUrl ||
      cloud.fileUrl ||
      "";
    const cover =
      src.coverImageUrl ||
      cloud.coverUrl ||
      "";
    const download = view ? asDownloadUrl(view) : "";
    const baseName = cloud.originalName || fileNameFromUrl(view) || "ficha_postulacion.pdf";
    const name = generateFileNameWithRazonSocial(baseName, "ficha_postulacion.pdf");
    const generatedAt = src.docsGeneratedAt || (ans?.generatedDocs?.length ? ans.generatedDocs[0]?.createdAt : null);
    return { view, cover, download, name, generatedAt };
  }, [src, ans, razonSocial]);

  const hasPdf = !!pdfInfo.view;

  // Información del PDF/DOCX de declaración jurada
  const declarationPdfInfo = useMemo(() => {
    const declarationPdf = ans?.declarationPdf || {};
    const view =
      src.declarationPdfUrl ||
      declarationPdf.viewUrl ||
      declarationPdf.secureUrl ||
      declarationPdf.downloadUrl ||
      "";
    const download = view ? asDownloadUrl(view) : "";
    const baseName = declarationPdf.originalName || fileNameFromUrl(view) || "declaracion_jurada.pdf";
    const name = generateFileNameWithRazonSocial(baseName, "declaracion_jurada.pdf");
    const cover =
      src?.declarationPdfCoverUrl ||
      declarationPdf.coverUrl ||
      "";
    // Detectar si es DOCX o PDF basándose en la extensión del nombre
    const isDocx = name.toLowerCase().endsWith('.docx') || view.toLowerCase().includes('.docx');
    const isPdf = name.toLowerCase().endsWith('.pdf') || view.toLowerCase().includes('.pdf') || (!isDocx && view);
    return { view, download, name, cover, isDocx, isPdf };
  }, [src, ans, razonSocial]);

  const hasDeclarationPdf = !!declarationPdfInfo.view;

  const cartaCandidates = [
    ans?.adjuntos?.carta_recomendacion,
    ans?.adjuntos?.recomendacion,
    src.cartaRecomendacionFile && [src.cartaRecomendacionFile],
    src.recommendationLetterFiles,
    src.cartaRecomendacion,
    src.files?.cartaRecomendacion,
    src.files?.recommendationLetter,
    src.attachments?.cartaRecomendacion,
    src.attachments?.recommendationLetter,
  ].filter(Boolean);

  const carpetaCandidates = [
    ans?.adjuntos?.carpeta_tributaria,
    src.carpetaTributariaFiles,
    src.carpetaTributaria,
    src.files?.carpetaTributaria,
    src.attachments?.carpetaTributaria,
    src.carpetaTributariaUrls,
    src.carpetaTributariaLinks,
  ].filter(Boolean);

  const logoCandidates = [
    ans?.adjuntos?.logo_alta,
    src.logoFiles,
    src.logoAlta,
    src.files?.logoAlta,
    src.files?.logo,
    src.attachments?.logo,
    src.logo,
    src.logoUrl,
  ].filter(Boolean);

  const cartaEntries      = collectFileEntries(cartaCandidates);
  const tributarioEntries = collectFileEntries(carpetaCandidates);
  const logoEntriesRaw    = collectFileEntries(logoCandidates);

  const heroLogo = useMemo(() => {
    if (!logoEntriesRaw.length) return null;
    const first = logoEntriesRaw[0];
    const href = normalizeImgUrl(first.href);
    return { ...first, href };
  }, [logoEntriesRaw]);

  const logoEntries = useMemo(
    () => logoEntriesRaw.map(e => ({ ...e, href: normalizeImgUrl(e.href) })),
    [logoEntriesRaw]
  );

  const info = useMemo(() => ({
    empresa: valOrDash(coalesce(src.razonSocial, src.nombreFantasia, ans?.empresa?.nombre_comercial, ans?.empresa?.razon_social)),
    rut: valOrDash(coalesce(src.rutEmpresa, ans?.empresa?.rut)),
    web: valOrDash(coalesce(src.paginaWeb, ans?.empresa?.pagina_web)),
    industria: valOrDash(coalesce(src.actividadRubro, ans?.empresa?.industria)),
    tamano: valOrDash(humanSize(coalesce(src.tamanoEmpresa, src.tamano, ans?.empresa?.tamano))),
    trabajadores: valOrDash(humanWorkers(coalesce(src.numeroTrabajadores, ans?.empresa?.numero_trabajadores))),
    periodicidad: valOrDash(humanPeriodicity(coalesce(src.periodicidadPago, ans?.empresa?.periodicidad_pago))),
    region: valOrDash(coalesce(src.region, ans?.empresa?.region)),
    comuna: valOrDash(coalesce(src.comuna, ans?.empresa?.comuna)),
    direccion: valOrDash(coalesce(src.domicilioEmpresa, src.direccionMatriz, ans?.empresa?.direccion_matriz)),
    motivo: valOrDash(coalesce(src.motivosIngreso, src.motivoIngreso, ans?.empresa?.motivo_ingreso)),
    aporte: valOrDash(coalesce(src.aporteRegion, ans?.empresa?.aporte_region)),
    valorMembresia: valOrDash(extraerNumero(coalesce(src.valorMembresia, ans?.empresa?.valor_membresia))),
  }), [src, ans]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/30 backdrop-blur-sm flex items-center justify-center p-3">
      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative">
          <div className="relative px-4 py-5 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-[#0b3a66] via-[#0b6b93] to-[#0488AF]" />
            <div className="pointer-events-none absolute -top-12 -right-10 w-56 h-56 bg-white/15 rounded-full blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 w-72 h-72 bg-black/10 rounded-full blur-3xl" />

            <div className="relative flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl bg-white/90 ring-2 ring-white/30 overflow-hidden flex items-center justify-center shrink-0">
                {heroLogo ? (
                  <img
                    src={cloudThumb(heroLogo.href, { w: 140, h: 140 })}
                    alt="Logo empresa"
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <ImageIcon className="w-8 h-8 text-slate-200" />
                )}
              </div>

              <div className="min-w-0 flex-1 text-white">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-semibold truncate">{info.empresa}</h3>
                  <DecisionBar status={src.status} />
                </div>
                <div className="text-xs text-white/90 truncate">
                  RUT: {info.rut} • {info.region}{info.region !== "—" ? " / " : ""}{info.comuna}
                  {detailLoading ? <span className="ml-2 text-amber-300">• Cargando detalle…</span> : null}
                </div>
              </div>

              <button
                onClick={onClose}
                className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-white"
                title="Cerrar"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="h-2 bg-gradient-to-b from-black/5 to-transparent" />
        </div>

        {/* Tabs */}
        <div className="px-3 py-2 bg-gray-50 border-b">
          <div className="flex flex-wrap gap-2 items-center">
            <TabButton active={tab === "DETAIL"} onClick={() => setTab("DETAIL")}>Detalle</TabButton>
            <TabButton active={tab === "FILES"} onClick={() => setTab("FILES")}>Adjuntos</TabButton>
            <TabButton active={tab === "MEMBRESIA"} onClick={() => setTab("MEMBRESIA")}>Valor de Membresía</TabButton>
            <TabButton active={tab === "DOCS"} onClick={() => setTab("DOCS")}>Documentos</TabButton>
            <div className="ml-auto">
              <TooltipHelper text="Detalle: Información completa de la empresa y contactos. Adjuntos: Documentos subidos por el postulante (carta, carpeta tributaria, logo). Valor de Membresía: Edita el valor en UF y genera la declaración jurada. Documentos: Ficha de postulación generada en PDF." />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="max-h-[78vh] overflow-y-auto">
          {/* ===== Detalle ===== */}
          {tab === "DETAIL" && (
            <div className="p-4 md:p-6 space-y-6">
              {/* Hero SOLO logo */}
              <section className="rounded-2xl border p-5 bg-white overflow-hidden">
                <div className="flex items-center gap-4">
                  <div className="w-28 h-28 rounded-2xl bg-slate-50 ring-1 ring-slate-200 overflow-hidden flex items-center justify-center">
                    {heroLogo ? (
                      <img
                        src={cloudThumb(heroLogo.href, { w: 400, h: 400 })}
                        alt="Logo empresa"
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <ImageIcon className="w-10 h-10 text-slate-300" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wide text-slate-500">Logo de la empresa</div>
                    <div className="mt-2 flex items-center gap-2">
                      {heroLogo && (
                        <a
                          href={heroLogo.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm shadow hover:from-[#071f3d] hover:to-[#03779C]"
                          title="Abrir logo de empresa"
                        >
                          Abrir <ExternalLink size={14} className="opacity-90" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* Empresa */}
              <section className="rounded-2xl border p-4 md:p-5 overflow-hidden">
                <SectionBannerPro
                  icon={<Building2 className="h-3.5 w-3.5" />}
                  title="Datos de la empresa"
                  subtitle="Identificación y atributos clave."
                  actions={
                    !editMode ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={startEdit}
                          className={
                            canEdit
                              ? "inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm shadow hover:from-[#071f3d] hover:to-[#03779C]"
                              : "inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed"
                          }
                          title={canEdit 
                            ? (allowEditWhenAccepted && status === "ACCEPTED" 
                                ? "Editar (modo especial: permitido para postulaciones aceptadas)" 
                                : "Editar")
                            : "Solo editable en estado Pendiente o cuando se permite edición especial"}
                          disabled={!canEdit}
                        >
                          <PencilLine size={16} /> Editar
                        </button>
                        <TooltipHelper text="Edita la información de la empresa, contactos y motivación. Solo disponible cuando la postulación está en estado Pendiente. Al guardar, se regenerará automáticamente el PDF de la ficha de postulación." />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditMode(false)}
                          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] text-sm"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={onSave}
                          disabled={saving || !src.id}
                          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 text-sm"
                          title="Guardar cambios"
                        >
                          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                          Guardar
                        </button>
                      </div>
                    )
                  }
                />

                <div className="mt-3">
                  {!editMode ? (
                    <CompanyReadView info={info} />
                  ) : (
                    <>
                      {saving && (
                        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 flex items-center gap-2">
                          <Loader2 size={16} className="animate-spin" />
                          <span>Guardando cambios y regenerando PDFs...</span>
                        </div>
                      )}
                      <EditCompanyForm
                        loadingTpl={loadingTpl}
                        tpl={tpl}
                        setField={setField}
                        calcularValorUF={calcularValorUF}
                        valorMembresiaEditadoManualRef={valorMembresiaEditadoManualRef}
                      />
                    </>
                  )}
                </div>
              </section>

              {/* Contactos */}
              {!editMode ? (
                <section className="rounded-2xl border p-4 overflow-hidden">
                  <SectionBannerPro
                    icon={<Users2 className="h-3.5 w-3.5" />}
                    title="Contactos"
                    subtitle="Titular, contacto operativo y cobranzas."
                  />
                  <div className="mt-3 grid md:grid-cols-3 gap-4">
                    <ContactCardPro title="Titular / Representante" data={{
                      nombre: valOrDash(coalesce(src.titularNombre, src.titular?.nombre, ans?.titular?.nombre)),
                      correo: valOrDash(coalesce(src.titularCorreo, src.titular?.correo, ans?.titular?.correo)),
                      movil:  valOrDash(coalesce(src.titularMovil,  src.titular?.movil,  ans?.titular?.movil)),
                      cargo:  valOrDash(coalesce(src.titularCargo,  src.titular?.cargo,  ans?.titular?.cargo)),
                    }} />
                    <ContactCardPro title="Contacto empresa" data={{
                      nombre: valOrDash(coalesce(src.contactoNombre, src.contacto?.nombre, ans?.contacto?.nombre)),
                      correo: valOrDash(coalesce(src.contactoCorreo, src.contacto?.correo, ans?.contacto?.correo)),
                      movil:  valOrDash(coalesce(src.contactoMovil,  src.contacto?.movil,  ans?.contacto?.movil)),
                      cargo:  valOrDash(coalesce(src.contactoCargo,  src.contacto?.cargo,  ans?.contacto?.cargo)),
                    }} />
                    <BillingCardPro title="Contacto de cobranzas" data={{
                      nombre:   valOrDash(coalesce(src.cobranzaNombre,   src.billingContactNombre,   ans?.cobranza?.nombre)),
                      correo:   valOrDash(coalesce(src.cobranzaCorreo,   src.billingContactCorreo,   ans?.cobranza?.correo)),
                      telefono: valOrDash(coalesce(src.cobranzaTelefono, src.billingContactTelefono, ans?.cobranza?.telefono)),
                      cargo:    valOrDash(coalesce(src.cobranzaCargo,                                ans?.cobranza?.cargo)),
                      area:     valOrDash(coalesce(src.cobranzaArea,                                 ans?.cobranza?.area)),
                    }} />
                  </div>
                </section>
              ) : (
                <EditContactsForm
                  loadingTpl={loadingTpl}
                  tpl={tpl}
                  setField={setField}
                />
              )}

              {/* Motivo + Aporte */}
              {!editMode ? (
                <section className="rounded-2xl border p-4 overflow-hidden">
                  <SectionBannerPro
                    icon={<Briefcase className="h-3.5 w-3.5" />}
                    title="Motivación para ser parte de CIDERE"
                    subtitle=""
                  />

                  <div className="mt-3 grid md:grid-cols-2 gap-4">
                    <div className="rounded-xl border bg-slate-50/60 p-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                        Motivación para ser parte de CIDERE
                      </div>
                      <div className="text-[15px] text-slate-800 whitespace-pre-wrap">
                        {info.motivo}
                      </div>
                    </div>

                    <div className="rounded-xl border bg-slate-50/60 p-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                        Aporte a la región
                      </div>
                      <div className="text-[15px] text-slate-800 whitespace-pre-wrap">
                        {info.aporte}
                      </div>
                    </div>
                  </div>
                </section>
              ) : (
                <EditMotivationForm
                  loadingTpl={loadingTpl}
                  tpl={tpl}
                  setField={setField}
                />
              )}
            </div>
          )}

          {/* ===== Adjuntos ===== */}
          {tab === "FILES" && (
            <div className="p-4 md:p-6">
              <div className="rounded-2xl border p-4 md:p-5 space-y-4 overflow-hidden bg-gradient-to-br from-sky-50 via-white to-indigo-50">
                <SectionBannerPro
                  icon={<FolderOpen className="h-3.5 w-3.5" />}
                  title="Adjuntos de la postulación"
                  subtitle="Carta, carpeta tributaria y logo."
                  actions={
                    <TooltipHelper text="Aquí puedes ver y descargar todos los documentos que el postulante adjuntó al enviar su solicitud: carta de recomendación (PDF), carpeta tributaria (múltiples PDFs) y logo de la empresa (imagen)." />
                  }
                />

                <FilesList
                  accent= "indigo"
                  icon={<FileText size={16} className="text-indigo-600" />}
                  title="Carta de recomendación"
                  entries={cartaEntries}
                  emptyHint="No se encontró carta de recomendación."
                />

                <FilesList
                  accent = "cyan"
                  icon={<FolderOpen size={16} className="text-cyan-600" />}
                  title="Carpeta tributaria"
                  entries={tributarioEntries}
                  emptyHint="No se encontró carpeta tributaria."
                />

                <FilesList
                  accent= "amber"
                  icon={<ImageIcon size={16} className="text-amber-600" />}
                  title="Logo (PNG/JPG)"
                  entries={logoEntries}
                  emptyHint="No se encontró logo."
                />
              </div>
            </div>
          )}

          {/* ===== Documentos (PDF generado) ===== */}
          {tab === "DOCS" && (
            <div className="p-4 md:p-6">
              <section className="rounded-2xl border p-4 md:p-5 overflow-hidden">
                <SectionBannerPro
                  icon={<FileText className="h-3.5 w-3.5" />}
                  title="Documentos generados"
                  subtitle="Ficha de postulación alojada en Cloudinary."
                  actions={
                    <TooltipHelper text="Documento PDF generado automáticamente con la información de la postulación. Se actualiza automáticamente cuando editas los datos de la empresa. Puedes verlo y descargarlo desde aquí." />
                  }
                />

                <div className="mt-3 grid md:grid-cols-3 gap-4">
                  {/* Preview / Portada */}
                  <div className="md:col-span-1">
                    <div className="rounded-xl border bg-slate-50/60 p-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Vista previa</div>
                      <div className="relative w-full rounded-lg border bg-white overflow-hidden">
                        {pdfInfo.cover ? (
                          <img
                            src={cloudThumb(pdfInfo.cover, { w: 600, h: 600 })}
                            alt="Portada PDF"
                            className="w/full h-auto object-contain"
                            loading="lazy"
                          />
                        ) : hasPdf ? (
                          <div className="aspect-[3/4]">
                            <object data={pdfInfo.view} type="application/pdf" className="w-full h-full" />
                          </div>
                        ) : (
                          <div className="aspect-[3/4] flex items-center justify-center text-slate-500">
                            Aún no hay PDF generado. Usa “Generar PDF”.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Datos + acciones */}
                  <div className="md:col-span-2">
                    <div className="rounded-xl border p-3 space-y-3">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <KV label="Nombre del archivo" value={pdfInfo.name || "—"} />
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {hasPdf && (
                          <>
                            <a
                              href={pdfInfo.view}
                              download={pdfInfo.name}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm shadow hover:from-[#071f3d] hover:to-[#03779C]"
                            >
                              Abrir <ExternalLink size={14} className="opacity-90" />
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* ===== Valor de Membresía ===== */}
          {tab === "MEMBRESIA" && (
            <div className="p-4 md:p-6">
              {loadingTpl && !tpl ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="animate-spin mr-2" /> Cargando datos de edición…
                </div>
              ) : (
                <section className="rounded-2xl border p-4 md:p-5 overflow-hidden">
                  <SectionBannerPro
                    icon={<FileText className="h-3.5 w-3.5" />}
                    title="Valor de Membresía"
                    subtitle={canEdit ? "Edita el valor de membresía en UF. El PDF de declaración jurada se generará automáticamente al guardar." : "El valor de membresía solo puede editarse cuando la postulación está en estado Pendiente."}
                    actions={
                      canEdit ? (
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => {
                              if (!allowEditMembresia) {
                                if (!tpl) loadTemplate();
                                setAllowEditMembresia(true);
                              } else {
                                setAllowEditMembresia(false);
                              }
                            }}
                            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm shadow hover:from-[#071f3d] hover:to-[#03779C]"
                            title="Editar valor de membresía"
                          >
                            <PencilLine size={16} />
                            {allowEditMembresia ? "Editando" : "Editar"}
                          </button>
                          <TooltipHelper text="Al editar el valor de membresía, el sistema rellenará automáticamente el valor según el tamaño de la empresa (Micro: 2 UF, Pequeña: 2,5 UF, Mediana: 5 UF, Grande: 10 UF). Puedes modificar este valor si es necesario. Al guardar, se generará automáticamente el PDF de la declaración jurada con el nuevo valor." />
                        </div>
                      ) : null
                    }
                  />

                  <div className="mt-3 space-y-3">
                    {/* Aviso no editable - Mostrado arriba */}
                    {!canEdit && (
                      <div className="inline-flex items-center gap-2 rounded-lg border-2 border-blue-300 bg-blue-50 px-4 py-2 text-xs sm:text-sm font-medium text-blue-800 shadow-sm">
                        <span className="text-base leading-none">ℹ️</span>
                        <span>
                          <span className="font-bold text-blue-900">No editable:</span> El valor de membresía solo puede editarse cuando la postulación está en estado <span className="font-bold text-blue-900">Pendiente</span>.
                        </span>
                      </div>
                    )}
                    {/* Formulario de edición - Mostrado arriba cuando se activa */}
                    {canEdit && allowEditMembresia && (
                      <div className="rounded-xl border p-3 bg-slate-50/60">
                        <div className="space-y-3">
                          <div className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800">
                            <strong>Auto-relleno automático:</strong> El valor se rellena automáticamente según el tamaño de la empresa. Puedes editarlo si lo deseas.
                            <ul className="mt-1.5 ml-4 list-disc space-y-0.5 text-[11px]">
                              <li>Micro: 2 UF</li>
                              <li>Pequeña: 2,5 UF</li>
                              <li>Mediana: 5 UF</li>
                              <li>Grande: 10 UF</li>
                            </ul>
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                              Valor de membresía (en UF)
                              <TooltipHelper text="Ingresa el valor de membresía en Unidades de Fomento (UF). Usa coma (,) para separar decimales. Ejemplo: 2,5 UF. El valor se rellenará automáticamente según el tamaño de la empresa, pero puedes editarlo si es necesario." />
                            </label>
                            <input
                              type="text"
                              placeholder="Ej: 2,5"
                              value={(tpl?.empresa?.valor_membresia || "").replace(/\./g, ',')}
                              onChange={(e) => {
                                // Solo permitir números y coma decimal (convertir punto a coma)
                                let valor = e.target.value.replace(/[^\d,]/g, '');
                                // Reemplazar punto por coma si se ingresa punto
                                valor = valor.replace(/\./g, ',');
                                // Asegurar solo una coma
                                const partes = valor.split(',');
                                if (partes.length > 2) {
                                  valor = partes[0] + ',' + partes.slice(1).join('');
                                }
                                setField("empresa.valor_membresia", valor);
                                // Marcar que el valor fue editado manualmente
                                valorMembresiaEditadoManualRef.current = true;
                              }}
                              className="w-full rounded-lg border px-2.5 py-1.5 bg-white text-sm"
                            />
                            <div className="mt-0.5 text-[11px] text-gray-500">
                              <strong>Nota:</strong> Usa coma (,) para separar decimales. Ejemplo: 2,5 UF (no uses punto).
                            </div>
                            {tpl?.empresa?.tamano && !tpl?.empresa?.valor_membresia && (
                              <div className="mt-0.5 text-[11px] text-amber-600">
                                Se rellenará automáticamente con: {calcularValorUF(tpl.empresa.tamano)?.replace(/\./g, ',') || "—"} UF
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                              Año de la declaración
                              <TooltipHelper text="Ingresa el año de la declaración jurada. Este valor se insertará automáticamente en el documento PDF donde esté el marcador {fecha}. Por defecto se usa el año actual." />
                            </label>
                            <input
                              type="text"
                              placeholder={`Ej: ${new Date().getFullYear()}`}
                              value={tpl?.empresa?.fecha || new Date().getFullYear().toString()}
                              onChange={(e) => {
                                // Solo permitir números (año de 4 dígitos máximo)
                                const fecha = e.target.value.replace(/[^\d]/g, '').slice(0, 4);
                                const añoActual = new Date().getFullYear().toString();
                                setField("empresa.fecha", fecha || añoActual);
                              }}
                              className="w-full rounded-lg border px-2.5 py-1.5 bg-white text-sm"
                            />
                            <div className="mt-0.5 text-[11px] text-gray-500">
                              Este valor se insertará en el documento donde esté el marcador {"{fecha}"}. Por defecto: {new Date().getFullYear()}
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 items-center">
                            <button
                              onClick={async () => {
                                if (!tpl) await loadTemplate();
                                
                                // Mostrar mensaje de carga
                                const loadingToast = Swal.fire({
                                  title: "Guardando valor de membresía...",
                                  html: "Por favor espera mientras se guarda el valor y se genera el PDF de declaración jurada.",
                                  allowOutsideClick: false,
                                  allowEscapeKey: false,
                                  showConfirmButton: false,
                                  didOpen: () => {
                                    Swal.showLoading();
                                  },
                                });
                                
                                try {
                                  await onSave();
                                } catch (e) {
                                  // El error ya se maneja en onSave
                                }
                              }}
                              disabled={saving || loadingTpl}
                              className="inline-flex items-center gap-2 h-8 px-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {saving ? (
                                <>
                                  <Loader2 size={13} className="animate-spin" />
                                  Guardando...
                                </>
                              ) : (
                                <>
                                  <Save size={13} />
                                  Guardar y generar PDF
                                </>
                              )}
                            </button>
                            <TooltipHelper text="Al hacer clic, se guardará el valor de membresía y el año de declaración, y se generará automáticamente un nuevo PDF de la declaración jurada con estos datos actualizados. El proceso puede tardar unos segundos." />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Información de tamaño, valor y año */}
                    <div className="rounded-xl border p-3 bg-slate-50/60">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <KV 
                          label="Tamaño de empresa" 
                          value={valOrDash(humanSize(src.tamanoEmpresa || ans?.empresa?.tamano || tpl?.empresa?.tamano || ""))} 
                        />
                        <KV 
                          label="Valor de membresía actual" 
                          value={valOrDash(extraerNumero(src.valorMembresia || ans?.empresa?.valor_membresia || tpl?.empresa?.valor_membresia) || "—")} 
                        />
                        <KV 
                          label="Año de la declaración" 
                          value={valOrDash(ans?.empresa?.fecha || tpl?.empresa?.fecha || new Date().getFullYear().toString())} 
                        />
                      </div>
                    </div>

                    {/* Vista previa del PDF/DOCX de declaración jurada */}
                    {hasDeclarationPdf && (
                      <div className="rounded-xl border bg-white overflow-hidden">
                        <div className="p-3">
                          <div className="grid md:grid-cols-3 gap-3">
                            {/* Preview / Portada */}
                            <div className="md:col-span-1">
                              <div className="rounded-xl border bg-slate-50/60 p-2">
                                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5">Vista previa</div>
                                <div className="relative w-full max-w-[150px] mx-auto rounded-lg border bg-white overflow-hidden">
                                  {declarationPdfInfo.isPdf && declarationPdfInfo.cover ? (
                                    <img
                                      src={cloudThumb(declarationPdfInfo.cover, { w: 150, h: 150 })}
                                      alt="Portada Declaración Jurada"
                                      className="w-full h-auto object-contain"
                                      loading="lazy"
                                    />
                                  ) : declarationPdfInfo.isPdf && declarationPdfInfo.view ? (
                                    <div className="aspect-[3/4] max-h-[200px]">
                                      <object data={declarationPdfInfo.view} type="application/pdf" className="w-full h-full" />
                                    </div>
                                  ) : declarationPdfInfo.isDocx ? (
                                    <div className="aspect-[3/4] max-h-[200px] flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
                                      <FileText className="w-4 h-4 text-blue-600" />
                                    </div>
                                  ) : (
                                    <div className="aspect-[3/4] max-h-[200px] flex items-center justify-center text-slate-500 bg-gray-50">
                                      <FileText className="w-4 h-4 text-gray-400" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Datos + acciones */}
                            <div className="md:col-span-2">
                              <div className="rounded-xl border bg-slate-50/60 p-3 space-y-3">
                                <div className="grid sm:grid-cols-2 gap-2">
                                  <KV label="Nombre del archivo" value={declarationPdfInfo.name || "—"} />
              
                                </div>

                                <div className="flex flex-wrap gap-2">
                                  <a
                                    href={declarationPdfInfo.view}
                                    download={declarationPdfInfo.name}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 h-8 px-3 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm shadow hover:from-[#071f3d] hover:to-[#03779C]"
                                  >
                                    <ExternalLink size={12} />
                                    {declarationPdfInfo.isDocx ? "Abrir Word" : "Ver PDF completo"}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

/* ------- Subcomponentes base ------- */
function Field({ label, full, children }) {
  return (
    <label className={`${full ? "md:col-span-2" : ""}`}>
      <div className="block text-xs text-gray-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
function validHttp(url = "") {
  return /^https?:\/\//i.test(url) ? url : "";
}

/* =================== Banner de sección =================== */
function SectionBannerPro({ title, subtitle, icon, actions = null }) {
  return (
    <div className="-mx-4 md:-mx-5 -mt-4 md:-mt-5">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b3a66] via-[#0b6b93] to-[#0488AF]" />
        <div className="relative px-4 md:px-5 py-3 text-white">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-7 shrink-0 items-center gap-2 rounded-full bg-white/90 text-[#0b3a66] px-2.5 text-[12px] font-semibold">
                  {icon} <span>Sección</span>
                </span>
                <div className="font-semibold">{title}</div>
              </div>
              {subtitle ? <div className="text-[12px] text-white/85 mt-0.5">{subtitle}</div> : null}
            </div>
            {actions ? <div className="shrink-0">{actions}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/* =================== Vista de empresa =================== */
function KV({ label, value, link, className = "" }) {
  const content = (
    <div className="text-[15px] text-slate-800 break-words">
      {value}
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#0488AF] underline"
        >
          Abrir
        </a>
      ) : null}
    </div>
  );
  return (
    <div className={className}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      {content}
    </div>
  );
}

function CompanyReadView({ info }) {
  const regionComuna =
    info.region === "—" && info.comuna === "—"
      ? "—"
      : [info.region, info.comuna].filter((s) => s && s !== "—").join(" / ");

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <KV label="RUT" value={info.rut} />
        <KV label="Industria" value={info.industria} />
        <KV label="Tamaño de la empresa" value={info.tamano} />
        <KV label="N° de trabajadores" value={info.trabajadores} />
        <KV label="Periodicidad de pago" value={info.periodicidad} />
        <KV label="Página web" value={info.web} />
        <KV label="Valor de membresía" value={info.valorMembresia} />
        <KV label="Región / Comuna" value={regionComuna} />
        <KV label="Dirección (casa matriz)" value={info.direccion} className="md:col-span-2" />
      </div>
    </div>
  );
}

/* =================== Formularios / ediciones (con SELECTS) =================== */
function EditCompanyForm({ loadingTpl, tpl, setField, calcularValorUF, valorMembresiaEditadoManualRef }) {
  if (loadingTpl) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <Loader2 className="animate-spin mr-2" /> Cargando datos de edición…
      </div>
    );
  }
  if (!tpl) return null;

  // ===== Opciones =====
  const SIZE_OPTS = [
    { value: "Micro",   label: "Micro" },
    { value: "Pequeña", label: "Pequeña" },
    { value: "Mediana", label: "Mediana" },
    { value: "Grande",  label: "Grande" },
  ];
  const WORKER_OPTS = [
    { value: "0-9",     label: "0–9" },
    { value: "10-49",   label: "10–49" },
    { value: "50-199",  label: "50–199" },
    { value: "200+",    label: "200+" },
  ];
  const INDUSTRY_OPTS = (INDUSTRIES || []).map((o) =>
    typeof o === "object" ? { value: o.value, label: o.label } : { value: o, label: o }
  );
  const PERIOD_OPTS = (PERIODICITIES && PERIODICITIES.length
    ? PERIODICITIES
    : ["Mensual","Semestral","Anual"]
  ).map((o) => (typeof o === "object" ? { value: o.value, label: o.label } : { value: o, label: o }));

  // ===== Regiones / Provincias / Comunas (normalizadas) =====
  const REGIONS = regionsData?.Regions || regionsData?.regions || [];

  const norm = (s = "") =>
    String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

  const getRegionName = (r) => r?.Region || r?.region || r?.name || "";
  const getProvinces  = (r) => r?.provinces || r?.Provinces || r?.Provincias || [];
  const getProvName   = (p) => p?.ProvinceName || p?.name || p?.provincia || "";
  const getCommunes   = (p) =>
    p?.districts || p?.Districts || p?.Comunas || p?.comunas || p?.Communes || p?.cities || p?.Cities || [];
  const getCommName   = (c) =>
    c?.name || c?.Nombre || c?.comuna || c?.ComunaName || c?.city || String(c) || "";

  const regionVal = tpl?.empresa?.region || "";
  const provVal   = tpl?.empresa?.provincia || "";
  const comunaVal = tpl?.empresa?.comuna || "";

  const provinces = React.useMemo(() => {
    const R = REGIONS.find((r) => norm(getRegionName(r)) === norm(regionVal));
    return (getProvinces(R) || []).map((p) => getProvName(p)).filter(Boolean);
  }, [REGIONS, regionVal]);

  const communes = React.useMemo(() => {
    const R = REGIONS.find((r) => norm(getRegionName(r)) === norm(regionVal));
    const P = (getProvinces(R) || []).find((p) => norm(getProvName(p)) === norm(provVal));
    return (getCommunes(P) || []).map((c) => getCommName(c)).filter(Boolean);
  }, [REGIONS, regionVal, provVal]);

  const inputClass  = "w-full rounded-lg border px-3 h-10";
  const selectClass = "w-full rounded-lg border px-3 h-10 bg-white";

  const Select = ({ value, onChange, options, placeholder = "Seleccione…", className = "" }) => {
    // Normalizar el valor para comparación (trim y case-insensitive)
    const normalizedValue = value ? String(value).trim() : "";
    const hasMatchingOption = normalizedValue && options.some((o) => {
      const optValue = String(o.value).trim();
      return optValue === normalizedValue || optValue.toLowerCase() === normalizedValue.toLowerCase();
    });
    
    // Si hay coincidencia, usar el valor exacto de la opción; si no, usar el valor normalizado
    const selectedValue = hasMatchingOption 
      ? options.find((o) => {
          const optValue = String(o.value).trim();
          return optValue === normalizedValue || optValue.toLowerCase() === normalizedValue.toLowerCase();
        })?.value || normalizedValue
      : normalizedValue;
    
    return (
      <select
        className={`${selectClass} ${className}`}
        value={selectedValue}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) =>
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        )}
        {/* ⬇️ condición para mostrar valor "no listado" */}
        {normalizedValue && !hasMatchingOption && (
          <option value={normalizedValue}>{normalizedValue}</option>
        )}
      </select>
    );
  };

  return (
    <div className="space-y-6">
      {/* EMPRESA - Ordenado igual que la vista de lectura */}
      <div className="grid md:grid-cols-2 gap-3">
          {/* 1. RUT */}
          <Field label="RUT">
            <input
              className={inputClass}
              value={tpl.empresa?.rut || ""}
              onChange={(e) => setField("empresa.rut", e.target.value)}
            />
          </Field>

          {/* 2. Industria */}
          <Field label="Industria">
            <Select
              value={tpl.empresa?.industria || ""}
              onChange={(v) => setField("empresa.industria", v)}
              options={INDUSTRY_OPTS}
            />
          </Field>

          {/* 3. Tamaño de empresa */}
          <Field label="Tamaño de empresa">
            <div className="flex items-center gap-2">
              <Select
                value={mapSizeToSpanish(tpl.empresa?.tamano || "")}
                onChange={(v) => {
                  // Convertir de español a inglés antes de guardar
                  const englishValue = mapSizeToEnglish(v);
                  setField("empresa.tamano", englishValue);
                  // Actualizar automáticamente el valor de membresía cuando cambia el tamaño
                  // PERO solo si NO fue editado manualmente desde "Valor de Membresía"
                  // Usar el valor en español para calcular UF
                  if (v && !valorMembresiaEditadoManualRef.current) {
                    const autoValue = calcularValorUF(v);
                    if (autoValue) {
                      setField("empresa.valor_membresia", autoValue);
                    }
                  }
                }}
                options={SIZE_OPTS}
                placeholder="Seleccione el tamaño de la empresa"
              />
              <TooltipHelper text="El tamaño de la empresa determina automáticamente el valor de membresía: Micro (2 UF), Pequeña (2,5 UF), Mediana (5 UF), Grande (10 UF). Este valor se puede modificar después en la pestaña 'Valor de Membresía'." />
            </div>
          </Field>

          {/* 4. N° de trabajadores */}
          <Field label="N° de trabajadores">
            <Select
              value={tpl.empresa?.numero_trabajadores || ""}
              onChange={(v) => setField("empresa.numero_trabajadores", v)}
              options={WORKER_OPTS}
            />
          </Field>

          {/* 5. Periodicidad de pago */}
          <Field label="Periodicidad de pago">
            <Select
              value={tpl.empresa?.periodicidad_pago || ""}
              onChange={(v) => setField("empresa.periodicidad_pago", v)}
              options={PERIOD_OPTS}
            />
          </Field>

          {/* 6. Página web */}
          <Field label="Página web">
            <input
              className={inputClass}
              value={tpl.empresa?.pagina_web || ""}
              onChange={(e) => setField("empresa.pagina_web", e.target.value)}
            />
          </Field>

          {/* 7. Región */}
          <Field label="Región">
            <div className="flex items-center gap-2">
              <Select
                value={regionVal}
                onChange={(v) => {
                  setField("empresa.region", v);
                  setField("empresa.provincia", "");
                  setField("empresa.comuna", "");
                }}
                options={REGIONS.map((r) => ({ value: getRegionName(r), label: getRegionName(r) }))}
              />
              <TooltipHelper text="Selecciona primero la región. Esto actualizará las opciones de provincia y comuna disponibles. Debes seleccionar región, provincia y comuna en ese orden." />
            </div>
          </Field>

          {/* 8. Provincia */}
          <Field label="Provincia">
            <div className="flex items-center gap-2">
              <Select
                value={provVal}
                onChange={(v) => {
                  setField("empresa.provincia", v);
                  setField("empresa.comuna", "");
                }}
                options={provinces.map((p) => ({ value: p, label: p }))}
                placeholder={provinces.length ? "Seleccione…" : "Primero selecciona una región"}
              />
              <TooltipHelper text="Selecciona la provincia correspondiente a la región elegida. Esto actualizará las opciones de comuna disponibles." />
            </div>
          </Field>

          {/* 9. Comuna */}
          <Field label="Comuna">
            <div className="flex items-center gap-2">
              <Select
                value={comunaVal}
                onChange={(v) => setField("empresa.comuna", v)}
                options={communes.map((c) => ({ value: c, label: c }))}
                placeholder={communes.length ? "Seleccione…" : "Primero selecciona región y provincia"}
              />
              <TooltipHelper text="Selecciona la comuna correspondiente a la provincia elegida. Debes haber seleccionado región y provincia primero." />
            </div>
          </Field>

          {/* 10. Dirección (casa matriz) - full width */}
          <Field label="Dirección (casa matriz)" full>
            <input
              className={inputClass}
              value={tpl.empresa?.direccion_matriz || ""}
              onChange={(e) => setField("empresa.direccion_matriz", e.target.value)}
            />
          </Field>

          {/* Campos adicionales que no están en la vista de lectura pero son necesarios */}
          <Field label="Razón social">
            <input
              className={inputClass}
              value={tpl.empresa?.razon_social || ""}
              onChange={(e) => setField("empresa.razon_social", e.target.value)}
            />
          </Field>

          <Field label="Nombre comercial">
            <input
              className={inputClass}
              value={tpl.empresa?.nombre_comercial || ""}
              onChange={(e) => setField("empresa.nombre_comercial", e.target.value)}
            />
          </Field>
      </div>
    </div>
  );
}

function EditContactsForm({ loadingTpl, tpl, setField }) {
  if (loadingTpl) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <Loader2 className="animate-spin mr-2" /> Cargando datos de edición…
      </div>
    );
  }
  if (!tpl) return null;

  const inputClass = "w-full rounded-lg border px-3 h-10";

  return (
    <section className="rounded-2xl border p-4 overflow-hidden">
      <SectionBannerPro
        icon={<Users2 className="h-3.5 w-3.5" />}
        title="Contactos"
        subtitle="Titular, contacto operativo y cobranzas."
      />
      <div className="mt-3 space-y-6">
        {/* TITULAR / REPRESENTANTE */}
        <div>
          <h5 className="font-semibold text-gray-800 mb-3">Titular / Representante</h5>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Nombre">
              <input
                className={inputClass}
                value={tpl.titular?.nombre || ""}
                onChange={(e) => setField("titular.nombre", e.target.value)}
              />
            </Field>
            <Field label="Cargo">
              <input
                className={inputClass}
                value={tpl.titular?.cargo || ""}
                onChange={(e) => setField("titular.cargo", e.target.value)}
              />
            </Field>
            <Field label="Correo">
              <input
                className={inputClass}
                value={tpl.titular?.correo || ""}
                onChange={(e) => setField("titular.correo", e.target.value)}
              />
            </Field>
            <Field label="Móvil">
              <input
                className={inputClass}
                value={tpl.titular?.movil || ""}
                onChange={(e) => setField("titular.movil", e.target.value)}
              />
            </Field>
          </div>
        </div>

        {/* CONTACTO EMPRESA */}
        <div>
          <h5 className="font-semibold text-gray-800 mb-3">Contacto empresa</h5>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Nombre">
              <input
                className={inputClass}
                value={tpl.contacto?.nombre || ""}
                onChange={(e) => setField("contacto.nombre", e.target.value)}
              />
            </Field>
            <Field label="Cargo">
              <input
                className={inputClass}
                value={tpl.contacto?.cargo || ""}
                onChange={(e) => setField("contacto.cargo", e.target.value)}
              />
            </Field>
            <Field label="Correo">
              <input
                className={inputClass}
                value={tpl.contacto?.correo || ""}
                onChange={(e) => setField("contacto.correo", e.target.value)}
              />
            </Field>
            <Field label="Móvil">
              <input
                className={inputClass}
                value={tpl.contacto?.movil || ""}
                onChange={(e) => setField("contacto.movil", e.target.value)}
              />
            </Field>
          </div>
        </div>

        {/* COBRANZA */}
        <div>
          <h5 className="font-semibold text-gray-800 mb-3">Contacto de cobranzas</h5>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Nombre">
              <input
                className={inputClass}
                value={tpl.cobranza?.nombre || ""}
                onChange={(e) => setField("cobranza.nombre", e.target.value)}
              />
            </Field>
            <Field label="Cargo">
              <input
                className={inputClass}
                value={tpl.cobranza?.cargo || ""}
                onChange={(e) => setField("cobranza.cargo", e.target.value)}
              />
            </Field>
            <Field label="Área">
              <input
                className={inputClass}
                value={tpl.cobranza?.area || ""}
                onChange={(e) => setField("cobranza.area", e.target.value)}
              />
            </Field>
            <Field label="Correo">
              <input
                className={inputClass}
                value={tpl.cobranza?.correo || ""}
                onChange={(e) => setField("cobranza.correo", e.target.value)}
              />
            </Field>
            <Field label="Teléfono" full>
              <input
                className={inputClass}
                value={tpl.cobranza?.telefono || ""}
                onChange={(e) => setField("cobranza.telefono", e.target.value)}
              />
            </Field>
          </div>
        </div>
      </div>
    </section>
  );
}

function EditMotivationForm({ loadingTpl, tpl, setField }) {
  if (loadingTpl) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <Loader2 className="animate-spin mr-2" /> Cargando datos de edición…
      </div>
    );
  }
  if (!tpl) return null;

  return (
    <section className="rounded-2xl border p-4 overflow-hidden">
      <SectionBannerPro
        icon={<Briefcase className="h-3.5 w-3.5" />}
        title="Motivación para ser parte de CIDERE"
        subtitle=""
      />
      <div className="mt-3 grid md:grid-cols-2 gap-4">
        <Field label="Motivo de ingreso" full>
          <textarea
            rows={5}
            className="w-full rounded-lg border px-3 py-2"
            value={tpl.empresa?.motivo_ingreso || ""}
            onChange={(e) => setField("empresa.motivo_ingreso", e.target.value)}
          />
        </Field>
        <Field label="Aporte a la región" full>
          <textarea
            rows={5}
            className="w-full rounded-lg border px-3 py-2"
            value={tpl.empresa?.aporte_region || ""}
            onChange={(e) => setField("empresa.aporte_region", e.target.value)}
          />
        </Field>
      </div>
    </section>
  );
}

/* =================== Tarjetas de contacto =================== */
function Row({ icon, label, value, link }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
        {icon}<span>{label}</span>
      </div>
      <div className="text-sm text-gray-800 break-words">
        {value}
        {link ? (
          <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#0488AF] underline">
            Abrir
          </a>
        ) : null}
      </div>
    </div>
  );
}

function ContactCardPro({ title, data }) {
  const mail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.correo) ? data.correo : "";
  const tel  = (data.movil || "").replace(/[^\d+]/g, "");

  return (
    <div className="rounded-xl border p-4">
      <div className="text-sm font-semibold text-gray-800 mb-2">{title}</div>
      <div className="space-y-2">
        <Row icon={<Users2 size={14} className="opacity-60" />} label="Nombre" value={data.nombre} />
        <Row icon={<Briefcase size={14} className="opacity-60" />} label="Cargo" value={data.cargo} />
        <Row icon={<ImageIcon size={14} className="opacity-60" />} label="Correo" value={data.correo} />
        <Row icon={<ImageIcon size={14} className="opacity-60" />} label="Móvil" value={data.movil} />
      </div>
    </div>
  );
}

function BillingCardPro({ title, data }) {
  const mail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.correo) ? data.correo : "";
  const tel  = ((data.telefono || "").replace(/[^\d+]/g, ""));

  return (
    <div className="rounded-xl border p-4">
      <div className="text-sm font-semibold text-gray-800 mb-2">{title}</div>
      <div className="space-y-2">
        <Row icon={<Users2 size={14} className="opacity-60" />} label="Nombre" value={data.nombre} />
        <Row icon={<Briefcase size={14} className="opacity-60" />} label="Cargo" value={data.cargo} />
        <Row icon={<FolderOpen size={14} className="opacity-60" />} label="Área" value={data.area} />
        <Row icon={<ImageIcon size={14} className="opacity-60" />} label="Correo" value={data.correo} />
        <Row icon={<ImageIcon size={14} className="opacity-60" />} label="Teléfono" value={data.telefono} />
      </div>
    </div>
  );
}

/* ------- Archivos ------- */

function FilesStrip({ icon, title, entries = [], emptyHint = "Sin archivos.", accent = "indigo" }) {
  const tones = {
    indigo: { ring: "ring-indigo-200", head: "from-indigo-500 to-sky-500" },
    cyan:   { ring: "ring-cyan-200",   head: "from-cyan-500 to-teal-500" },
    amber:  { ring: "ring-amber-200",  head: "from-amber-500 to-orange-500" },
  }[accent] || { ring: "ring-slate-200", head: "from-slate-500 to-slate-400" };

  const [idx, setIdx] = React.useState(0);
  const has = entries.length > 0;
  const current = has ? entries[Math.min(idx, entries.length - 1)] : null;

  return (
    <div className={`rounded-xl border p-3 bg-white ring-1 ${tones.ring}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-flex items-center gap-2 px-2 py-1 rounded-md text-xs font-semibold text-white bg-gradient-to-r ${tones.head}`}>
            {icon}<span className="truncate">{title}</span>
          </span>

          {entries.length > 1 && (
            <select
              value={idx}
              onChange={(e) => setIdx(Number(e.target.value))}
              className="ml-2 h-8 rounded-md border px-2 text-xs bg-white"
              title="Elegir archivo"
            >
              {entries.map((_, i) => <option key={i} value={i}>Archivo {i + 1}</option>)}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <a
            href={current?.href || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className={
              has
                ? "inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm shadow hover:from-[#071f3d] hover:to-[#03779C]"
                : "inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-gray-200 text-gray-400 border border-gray-300 opacity-50 pointer-events-none text-sm"
            }
          >
            Abrir <ExternalLink size={14} className={has ? "opacity-90" : "opacity-50"} />
          </a>
        </div>
      </div>

      {!has && <div className="text-sm text-slate-600 mt-2">{emptyHint}</div>}
    </div>
  );
}

/* Alias de compatibilidad para evitar errores si quedó alguna referencia antigua */
function FilesList(props) {
  return <FilesStrip {...props} />;
}
